import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function CsvImportButton({ moduleKey, onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/modules/${moduleKey}/template`;
    // Cookie-based auth (same-origin /api) — fetch + blob download.
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${moduleKey}-template.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => toast.error("Template download failed"));
  };

  const doImport = async () => {
    if (!file) return toast.error("Please choose a CSV file");
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/modules/${moduleKey}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.inserted > 0) {
        toast.success(`Imported ${data.inserted} records`);
        onImported && onImported();
      } else {
        toast.error("No records imported. See details.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="csv-import-button">
        <Upload className="h-4 w-4 mr-2" /> Import CSV
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to add multiple records at once. Column headers must match the module fields.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <button
              type="button"
              onClick={downloadTemplate}
              className="w-full flex items-center justify-between p-3 rounded-md border border-slate-200 hover:bg-slate-50 text-left"
              data-testid="csv-template-download"
            >
              <div>
                <div className="text-sm font-semibold text-slate-900">Download template</div>
                <div className="text-xs text-slate-500">Includes all columns + a hint row.</div>
              </div>
              <Download className="h-4 w-4 text-slate-600" />
            </button>

            <div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="csv-file-input"
              />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-200 hover:border-slate-400 rounded-md p-6 text-center transition-colors"
                data-testid="csv-pick-file"
              >
                <Upload className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                <div className="text-sm font-medium text-slate-700">
                  {file ? file.name : "Click to select a CSV file"}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {file ? `${(file.size / 1024).toFixed(1)} KB` : "CSV files only"}
                </div>
              </button>
            </div>

            {result && (
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 space-y-1.5 text-sm" data-testid="csv-import-result">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-semibold">{result.inserted} records imported</span>
                </div>
                {result.skipped > 0 && (
                  <div className="text-xs text-slate-500">{result.skipped} rows skipped (empty or template hint row).</div>
                )}
                {result.errors?.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center gap-1.5 text-amber-700 text-xs font-semibold">
                      <AlertCircle className="h-3.5 w-3.5" /> {result.errors.length} issues
                    </div>
                    <ul className="mt-1 text-xs text-slate-600 max-h-24 overflow-y-auto list-disc list-inside">
                      {result.errors.slice(0, 15).map((e, i) => (
                        <li key={`${i}::${e}`}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="csv-cancel">Close</Button>
            <Button
              onClick={doImport}
              disabled={!file || importing}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="csv-do-import"
            >
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
